const BASE_URL = require('../config');
import React, { useState, useEffect } from 'react';

const API_URL_ASSEMBLIES = BASE_URL + '/api/assemblies';

const DEFAULT_COLS = ['id', 'client_name', 'client_phone', 'client_details'];
const COL_DEFS = {
  id: { label: 'Сборка', classes: 'w-24' },
  client_name: { label: 'ФИО' },
  client_phone: { label: 'Телефон', classes: 'w-48' },
  client_details: { label: 'Доп. информация' }
};

const InlineActions = ({ onSave, onCancel }) => (
  <div className="flex space-x-1 ml-2">
    <button onClick={onSave} className="bg-emerald-500 text-white rounded hover:bg-emerald-600 flex items-center justify-center w-6 h-6 shadow-sm transition-transform hover:scale-105 shrink-0">✓</button>
    <button onClick={onCancel} className="bg-red-400 text-white rounded hover:bg-red-500 flex items-center justify-center w-6 h-6 shadow-sm transition-transform hover:scale-105 shrink-0">✕</button>
  </div>
);

export default function ClientsPage() {
  const [assemblies, setAssemblies] = useState([]);
  const [previewAssembly, setPreviewAssembly] = useState(null);

  const [editingCell, setEditingCell] = useState(null);
  const [cellEditValue, setCellEditValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const fetchBuilds = async () => {
    try {
      const res = await fetch(API_URL_ASSEMBLIES, { headers: { 'tg-id': window.localStorage.getItem('tg_user') || '' } });
      if (res.ok) setAssemblies(await res.json());
    } catch (error) { 
      console.error('Ошибка:', error); 
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchBuilds(); }, []);

  const handlePreviewAssembly = async (e, id) => {
    e.stopPropagation();
    const asm = assemblies.find(a => a.id === id);
    if (asm) setPreviewAssembly(asm);
  };

  const startCellEdit = (build, field) => {
    setEditingCell({ id: build.id, field });
    setCellEditValue(build[field] || '');
  };

  const cancelCellEdit = (e) => {
    if(e) e.stopPropagation();
    setEditingCell(null); setCellEditValue('');
  };

  const saveCellEdit = async (e) => {
    if(e) e.stopPropagation();
    if (!editingCell) return;
    const { id, field } = editingCell;
    const build = assemblies.find(b => b.id === id);
    if (!build) return;

    const payload = {
      name: build.name, sellingPrice: build.sellingPrice, custom_status: build.custom_status,
      sale_date: build.sale_date, comment: build.comment, component_ids: build.components.map(c => c.id),
      client_name: build.client_name, client_phone: build.client_phone, client_details: build.client_details
    };
    payload[field] = cellEditValue;

    try {
      await fetch(`${API_URL_ASSEMBLIES}/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'tg-id': window.localStorage.getItem('tg_user') || '' }, body: JSON.stringify(payload) });
      setEditingCell(null);
      fetchBuilds();
    } catch (error) { alert('Ошибка сохранения'); }
  };

  const renderCell = (colId, build) => {
    const isCellEditing = editingCell?.id === build.id && editingCell?.field === colId;
    const cellClass = `px-4 py-3 cursor-pointer hover:bg-indigo-50/70 transition-colors border-r border-slate-200 last:border-r-0 text-left text-slate-700`;
    const handleClick = () => { if (!isCellEditing && colId !== 'id') startCellEdit(build, colId); };

    if (isCellEditing) {
      return (
        <td key={colId} className="px-4 py-3 bg-indigo-50 border-r border-slate-200 last:border-r-0">
          <div className="flex items-center w-full min-w-[150px]">
            <input type="text" value={cellEditValue} onChange={e => setCellEditValue(e.target.value)} autoFocus className="w-full border border-indigo-400 p-1.5 rounded text-xs outline-none shadow-inner text-left" />
            <InlineActions onSave={saveCellEdit} onCancel={cancelCellEdit} />
          </div>
        </td>
      );
    }

    switch(colId) {
      case 'id': return <td key={colId} className="px-4 py-3 border-r border-slate-200 text-left"><button onClick={(e) => handlePreviewAssembly(e, build.id)} className="px-2 py-1 rounded bg-indigo-100 text-indigo-700 font-bold transition-all hover:ring-2 hover:ring-offset-1 hover:ring-indigo-300">#{build.id}</button></td>;
      case 'client_name': return <td key={colId} onClick={handleClick} className={`${cellClass} font-bold`} title="Нажмите для редактирования">{build.client_name || '-'}</td>;
      case 'client_phone': return <td key={colId} onClick={handleClick} className={cellClass} title="Нажмите для редактирования">{build.client_phone || '-'}</td>;
      case 'client_details': return <td key={colId} onClick={handleClick} className={`${cellClass} min-w-[200px] truncate`} title="Нажмите для редактирования">{build.client_details || '-'}</td>;
      default: return null;
    }
  };

  // Фильтруем: показываем только тех, у кого заполнено хотя бы одно поле
  const clients = assemblies.filter(a => a.client_name || a.client_phone || a.client_details);

  if (isLoading) return <div className="flex justify-center items-center h-64"><p className="text-slate-400 font-medium">Загрузка данных...</p></div>;

  return (
    <div className="max-w-[1200px] mx-auto pb-48 relative mt-0 px-2 mt-0 mt-0">
      <div className="flex justify-between items-center mb-1 h-[32px]">
        <h1 className="text-[11px] font-bold text-slate-700">База клиентов</h1>
        <div className="text-sm text-slate-500 font-medium bg-white px-4 py-2 rounded-lg shadow-sm border border-slate-200">
          Всего клиентов: <span className="font-bold text-indigo-600 text-lg ml-1">{clients.length}</span>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-x-auto">
        <table className="min-w-full text-left whitespace-nowrap text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {DEFAULT_COLS.map(colId => (
                <th key={colId} className={`px-4 py-3 text-slate-500 font-bold uppercase text-left border-r border-slate-200 last:border-r-0 tracking-wide ${COL_DEFS[colId].classes || ''}`}>
                  {COL_DEFS[colId].label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 ? (
               <tr><td colSpan={DEFAULT_COLS.length} className="px-6 py-12 text-center text-slate-500 bg-slate-50/50">База пуста. Добавьте информацию о клиенте в разделе "Сборка ПК", и она появится здесь.</td></tr>
            ) : clients.map((build) => (
              <tr key={build.id} className="border-b border-slate-200 hover:bg-slate-50 transition-colors">
                {DEFAULT_COLS.map(colId => renderCell(colId, build))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {previewAssembly && (
        <div className="fixed inset-0 bg-slate-900/60 z-[110] flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setPreviewAssembly(null)}>
          <div className="bg-white rounded-2xl w-full max-w-xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-indigo-50">
              <div className="flex items-center space-x-3"><span className="bg-indigo-600 text-white font-mono px-2 py-1 rounded text-xs font-bold">#{previewAssembly.id}</span><h2 className="text-lg font-bold text-slate-800">Информация о сборке</h2></div>
              <button onClick={() => setPreviewAssembly(null)} className="text-slate-400 hover:text-red-500 font-bold text-xl leading-none">&times;</button>
            </div>
            <div className="p-5 overflow-y-auto">
              <h3 className="text-xl font-bold text-slate-800 mb-4">{previewAssembly.name}</h3>
              {previewAssembly.comment && <p className="text-sm text-slate-500 mb-4 p-3 bg-slate-50 rounded italic border-l-4 border-indigo-300">{previewAssembly.comment}</p>}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-blue-50 border border-blue-100 p-3 rounded-lg"><span className="block text-[10px] uppercase font-bold text-blue-500 mb-1">Цена продажи</span><span className="text-lg font-bold text-blue-700">{previewAssembly.sellingPrice} ₽</span></div>
                <div className="bg-slate-50 border border-slate-200 p-3 rounded-lg"><span className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Закупка</span><span className="text-lg font-bold text-slate-700">{previewAssembly.cost} ₽</span></div>
              </div>
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 border-b border-slate-100 pb-1">Комплектующие внутри:</h4>
              <div className="space-y-1.5">
                {previewAssembly.components?.map(c => (
                  <div key={c.id} className="flex justify-between items-center p-2 bg-slate-50 rounded hover:bg-slate-100 transition-colors text-sm">
                    <div><span className="text-slate-400 font-mono text-xs mr-2">#{c.id}</span><span className="font-medium text-slate-700">{c.name}</span></div>
                    <span className="text-emerald-600 font-semibold">{c.purchase_price} ₽</span>
                  </div>
                ))}
                {(!previewAssembly.components || previewAssembly.components.length === 0) && <div className="text-slate-500 text-xs">Нет деталей.</div>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
